import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, LocateFixed } from 'lucide-react';
import { createAddress, updateAddress } from '@/api/shop';
import { useEvents } from '@/context/EventContext';
import { friendlyError } from '@/lib/apiError';

/** Field errors from the API: `{errors: [...]}` or `{errors: {errors: [...]}}` (Vine). */
const apiFieldErrors = (err) => {
  const e = err?.response?.data?.errors;
  const list = Array.isArray(e) ? e : Array.isArray(e?.errors) ? e.errors : [];
  return list.filter((x) => x && x.field);
};

/**
 * Minimal delivery-address form on the existing customer address API, for a
 * new address or (with `address`) editing one.
 *
 * A shop delivery only needs the PIN, so coordinates come from the device
 * when the shopper allows it and are otherwise sent as null — never a guessed
 * point (a made-up pin would mis-route anything that later uses it).
 */
const AddressForm = ({ address = null, onSaved, onCancel }) => {
  const { selectedCity } = useEvents();
  const editing = Boolean(address?.id);
  const [form, setForm] = useState(() => ({
    address_line: address?.addressLine || '',
    landmark: address?.landmark || '',
    city: address?.city || selectedCity?.name || '',
    state: address?.state || 'Rajasthan',
    postal_code: address?.postalCode || '',
    is_default: editing ? Boolean(address.isDefault) : true,
  }));
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const locate = () => {
    if (!navigator.geolocation) {
      toast.error('Location is not available on this device');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setErrors((e) => ({ ...e, location: undefined }));
        setLocating(false);
        toast.success('Location added');
      },
      () => {
        setLocating(false);
        toast.error('We could not get your location');
      },
      { timeout: 8000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.address_line.trim().length < 5) errs.address_line = 'Enter the house number, street and area';
    if (form.city.trim().length < 2) errs.city = 'Enter your city';
    if (form.state.trim().length < 2) errs.state = 'Enter your state';
    if (!/^\d{6}$/.test(form.postal_code.trim())) errs.postal_code = 'Enter a 6-digit PIN code';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      const payload = {
        address_line: form.address_line.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        country: 'India',
        postal_code: form.postal_code.trim(),
        is_default: form.is_default,
      };
      if (form.landmark.trim() || editing) payload.landmark = form.landmark.trim();
      if (coords) Object.assign(payload, coords);
      // New address without a device fix: say so (null), never invent a point.
      // An edit leaves the stored coordinates alone unless a new fix was taken.
      else if (!editing) Object.assign(payload, { latitude: null, longitude: null });
      const saved = editing ? await updateAddress(address.id, payload) : await createAddress(payload);
      toast.success(editing ? 'Address updated' : 'Address saved');
      onSaved?.(saved);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (fields.some((f) => f.field === 'latitude' || f.field === 'longitude')) {
        setErrors((e) => ({ ...e, location: 'Add your location for this address — tap “Use my current location”.' }));
        toast.error('Add your location to save this address');
      } else {
        toast.error(fields[0]?.message || friendlyError(err, 'Address could not be saved'));
      }
    } finally {
      setSaving(false);
    }
  };

  const field = (k, label, props = {}) => (
    <label className="block">
      <span className="lk-label">{label}</span>
      <input value={form[k]} onChange={set(k)} className={`lk-input mt-1 ${errors[k] ? 'border-danger' : ''}`} {...props} />
      {errors[k] && <span className="mt-1 block text-xs text-danger">{errors[k]}</span>}
    </label>
  );

  return (
    <form onSubmit={submit} className="space-y-3" noValidate>
      {field('address_line', 'House no., street, area', { autoComplete: 'street-address', placeholder: '12 Rani Bazar' })}
      {field('landmark', 'Landmark (optional)', { placeholder: 'Near Kote Gate' })}
      <div className="grid grid-cols-2 gap-3">
        {field('postal_code', 'PIN code', { inputMode: 'numeric', maxLength: 6, autoComplete: 'postal-code', placeholder: '334001' })}
        {field('city', 'City', { autoComplete: 'address-level2' })}
      </div>
      {field('state', 'State', { autoComplete: 'address-level1' })}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={locate} disabled={locating} className="lk-focus inline-flex items-center gap-1.5 rounded text-sm font-semibold text-brand-secondary">
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
          {coords ? 'Location added' : editing ? 'Update location' : 'Use my current location'}
        </button>
        <label className="inline-flex items-center gap-2 text-sm text-ink-700">
          <input type="checkbox" checked={form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} className="h-4 w-4 accent-brand-secondary" />
          Make default
        </label>
      </div>
      {errors.location && <p className="text-xs text-danger">{errors.location}</p>}
      <div className="flex gap-3 pt-1">
        {onCancel && (
          <button type="button" onClick={onCancel} className="lk-btn-ghost flex-1">
            Cancel
          </button>
        )}
        <button type="submit" disabled={saving} className="lk-btn-primary flex-1">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {editing ? 'Update address' : 'Save address'}
        </button>
      </div>
    </form>
  );
};

export default AddressForm;
